import {
  type GroupLink,
  type GroupLinkAxis,
  toAxis,
} from 'tabview/assistantBlocks/groupLinks';
import { findNode } from 'tabview/assistantBlocks/arrangeableNodes';
import type { BlocksViewNode } from '../types';
import { isGroup } from '../viewModel$/util';

const clearLinks = (node: BlocksViewNode): void => {
  delete node.alignedWith;
  node.children?.forEach(clearLinks);
};

const AXES = ['col', 'row'] as const;

/** The groups the links on one axis connect, each set sharing that axis's tracks. */
const connectedGroups = (links: GroupLink[]): string[][] => {
  const parent = new Map<string, string>();
  const find = (id: string): string => {
    const up = parent.get(id) ?? id;
    if (up === id) return id;
    const top = find(up);
    parent.set(id, top);
    return top;
  };
  links.forEach(({ sourceGroupId, targetGroupId }) =>
    parent.set(find(sourceGroupId), find(targetGroupId))
  );
  const ids = links.flatMap(({ sourceGroupId, targetGroupId }) => [
    sourceGroupId,
    targetGroupId,
  ]);
  return [...Map.groupBy(new Set(ids), find).values()];
};

/** Marks each set of linked groups as sharing its axis's tracks, replacing old marks. */
export const linkGroups = (root: BlocksViewNode, links: GroupLink[]): void => {
  clearLinks(root);
  AXES.forEach(axis =>
    connectedGroups(links.filter(({ side }) => toAxis(side) === axis)).forEach(
      ids => {
        const members = ids
          .map(id => findNode(root, id))
          .filter(
            (member): member is BlocksViewNode =>
              member !== null && isGroup(member)
          );
        if (members.length < 2) return;
        members.forEach(member => {
          member.alignedWith = member.alignedWith ?? { col: [], row: [] };
          member.alignedWith[axis] = members.filter(other => other !== member);
        });
      }
    )
  );
};

/**
 * The links in the order to place them: a link whose target is itself lined up with another group
 * comes after that link, so the target's blocks are in place first. A cycle keeps its given order.
 */
export const inPlacementOrder = (links: GroupLink[]): GroupLink[] => {
  const placed: GroupLink[] = [];
  const visiting = new Set<GroupLink>();
  const visit = (link: GroupLink): void => {
    if (placed.includes(link) || visiting.has(link)) return;
    visiting.add(link);
    links
      .filter(other => other.sourceGroupId === link.targetGroupId)
      .forEach(visit);
    placed.push(link);
  };
  links.forEach(visit);
  return placed;
};

type Track = { start: number; span: number };

const trackOf = (node: BlocksViewNode, axis: GroupLinkAxis): Track =>
  axis === 'col'
    ? { start: node.col, span: node.colSpan }
    : { start: node.row, span: node.rowSpan };

const setCell = (
  node: BlocksViewNode,
  axis: GroupLinkAxis,
  along: number,
  across: number
): void => {
  const [col, row] = axis === 'col' ? [along, across] : [across, along];
  node.col = col;
  node.row = row;
  node.colSpan = 1;
  node.rowSpan = 1;
};

/** The node track a stacked block takes: the view keeps blocks on odd tracks. */
const nodeTrack = (index: number): number => 2 * index + 1;

/**
 * Places each child of the source group on the track of the first target child it relates to, in
 * the target's order along the axis, stacking those that share one. Children that relate to none
 * follow on the tracks after the target's last.
 */
export const alignLinkedChildren = (
  root: BlocksViewNode,
  { sourceGroupId, targetGroupId, side, relatedTargets }: GroupLink
): void => {
  const axis = toAxis(side);
  const source = findNode(root, sourceGroupId);
  const target = findNode(root, targetGroupId);
  if (!source?.children || !target?.children) return;
  const targetById = new Map(target.children.map(child => [child.id, child]));
  const anchorTrack = (childId: string): number | null => {
    const tracks = (relatedTargets[childId] ?? []).flatMap(id => {
      const node = targetById.get(id);
      return node ? [trackOf(node, axis).start] : [];
    });
    return tracks.length ? Math.min(...tracks) : null;
  };
  const anchored = source.children.flatMap(child => {
    const track = anchorTrack(child.id);
    return track === null ? [] : [{ child, track }];
  });
  Map.groupBy(anchored, ({ track }) => track).forEach((stacked, track) =>
    stacked.forEach(({ child }, index) =>
      setCell(child, axis, track, nodeTrack(index))
    )
  );
  const end = Math.max(
    0,
    ...target.children.map(child => {
      const { start, span } = trackOf(child, axis);
      return start + span;
    })
  );
  source.children
    .filter(child => anchorTrack(child.id) === null)
    .forEach((child, index) =>
      setCell(child, axis, end + 1 + 2 * index, nodeTrack(0))
    );
};
